import { useContext, useEffect, useState } from 'react';
import Fade from '@mui/material/Fade';
import LinearProgress from '@mui/material/LinearProgress';
import SocketIOContext from '../context/socketIOContext';
import {
  subscribeRequestActivity,
  markSlowRequest,
} from '../utils/requestActivityStore';

// Only show the bar once a request has been running for a moment, so quick
// (cache-hit) responses never make it flash.
const SHOW_DELAY_MS = 400;
// How long the fade in/out takes - the bar appears and leaves gently instead of
// popping in and out of view.
const FADE_MS = 300;
// Indeterminate sweep duration. MUI's default (2.1s) reads hectic for ordinary
// loading, so both bars run slower; bar2 keeps MUI's proportional head start.
const SWEEP_MS = 2600;
const SWEEP_DELAY_MS = 1425;

// A thin fixed progress bar at the very top of the viewport. It appears while
// requests the user is waiting for are in flight (routine background traffic
// such as counter badges passes `background: true` to useHttp and is ignored)
// and turns amber once the backend signals - over the WebSocket, the instant a
// cache miss is detected - that the request has to query the database, so the
// user sees "this is slow because of the DB" while still waiting, without a
// popup or header clutter.
export const RequestActivityBar = () => {
  const socket = useContext(SocketIOContext);
  const [{ active, slow, revalidating }, setState] = useState({
    active: 0,
    slow: false,
    revalidating: false,
  });
  const [delayedActive, setDelayedActive] = useState(false);

  // Mirror the request-activity store.
  useEffect(() => subscribeRequestActivity(setState), []);

  // Delay showing the bar for in-flight requests so fast requests stay invisible.
  useEffect(() => {
    if (active > 0) {
      const timer = setTimeout(() => setDelayedActive(true), SHOW_DELAY_MS);
      return () => clearTimeout(timer);
    }
    setDelayedActive(false);
    return undefined;
  }, [active]);

  // Early cache-miss signal -> mark the current activity slow (amber).
  useEffect(() => {
    if (!socket || typeof socket.on !== 'function') return undefined;
    const off = socket.on('cache:slow-query', () => markSlowRequest());
    return () => {
      if (typeof off === 'function') off();
    };
  }, [socket]);

  // Show the bar while a request is in flight OR while cached data is being
  // reconciled with the database in the background.
  const visible = delayedActive || revalidating;
  // Amber means "the database is involved": a slow query or a background
  // reconciliation of cached data.
  const amber = slow || revalidating;

  return (
    <Fade in={visible} timeout={FADE_MS} unmountOnExit>
      <LinearProgress
        color={amber ? 'warning' : 'primary'}
        aria-label={
          revalidating
            ? 'Abgleich mit Datenbank läuft'
            : slow
              ? 'Datenbankabfrage läuft'
              : 'Lädt'
        }
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          height: 2,
          zIndex: theme => theme.zIndex.snackbar + 1,
          // Ordinary loading stays in the corner of the eye; the database
          // states keep full contrast because they carry a message. The opacity
          // sits on the bars, not the root - Fade owns the root's inline one.
          '& .MuiLinearProgress-bar': { opacity: amber ? 1 : 0.7 },
          '& .MuiLinearProgress-bar1Indeterminate': {
            animationDuration: `${SWEEP_MS}ms`,
          },
          '& .MuiLinearProgress-bar2Indeterminate': {
            animationDuration: `${SWEEP_MS}ms`,
            animationDelay: `${SWEEP_DELAY_MS}ms`,
          },
          // No travelling highlight - the static track carries the same message.
          '@media (prefers-reduced-motion: reduce)': {
            '& .MuiLinearProgress-bar': { animation: 'none' },
          },
        }}
      />
    </Fade>
  );
};

export default RequestActivityBar;
